import { useEffect } from "react";
import { colorMapping, drawColorbar, drawTicksAndLabels } from "../js/colorUtils";
import {
    singleMapInitialize,
    griddedMapsInitialize,
    removeGriddedMaps,
    removeSingleMap
} from '../js/mapUtils.js';
import { slider, windSlider } from '../js/sliderUtils.js';
import {
    generateTimeZoneOptions,
    dateText,
    initializations,
} from '../js/dateUtils.js';
import $ from 'jquery';
import maplibregl from 'maplibre-gl';

/**
 * Apply a callback to all active maps (single + gridded)
 */
function applyToAllMaps(singleMapRef, griddedMapRefs, callback) {
    if (singleMapRef.current) {
        callback(singleMapRef.current);
    }
    if (griddedMapRefs.current) {
        griddedMapRefs.current.forEach(map => {
            if (map) callback(map);
        });
    }
}

/**
 * Populate timezone dropdown options on mount
 */
export const useTimeZoneOptionsEffect = (setTimeZoneOptions) => {
    useEffect(() => {
        setTimeZoneOptions(generateTimeZoneOptions());
    }, [setTimeZoneOptions]);
};

/**
 * Update initialization and valid time strings when relevant state changes
 */
export const useDateTextEffect = (selectedInit, timeZone, fhour, setInitstr, setTiming, setValidstr) => {
    useEffect(() => {
        dateText(selectedInit, timeZone, fhour, setInitstr, setTiming, setValidstr);
    }, [timeZone, selectedInit, fhour, setInitstr, setTiming, setValidstr]);
};

/**
 * Set default initialization option when options are loaded
 */
export const useInitializationOptionsEffect = (initializationOptions, selectedInit, setSelectedInit) => {
    useEffect(() => {
        if (!selectedInit && initializationOptions.length > 0) {
            setSelectedInit(initializationOptions[0]);
        }
    }, [initializationOptions, selectedInit, setSelectedInit]);
};

/**
 * Keep input field value synced to forecast hour
 */
export const useInputValueEffect = (fhour, setInputValue) => {
    useEffect(() => {
        setInputValue(fhour);
    }, [fhour, setInputValue]);
};

/**
 * Redraw colorbar when selected variable changes
 */
export const useColorbarEffect = (selectedVariable, cmapSpacing, setCmapSpacing) => {
    useEffect(() => {
        const { minValue, maxValue, colorMap, reverse, levels, bounded } = colorMapping[selectedVariable];
        drawColorbar(selectedVariable, colorMap, reverse, minValue, maxValue, levels, bounded, cmapSpacing, setCmapSpacing);
    }, [selectedVariable]);
};

/**
 * Draw tick labels when colorbar spacing is set
 */
export const useTicksAndLabelsEffect = (selectedVariable, cmapSpacing) => {
    useEffect(() => {
        const { minValue, maxValue, reverse, levels, ticks, decimals, units, bounded } = colorMapping[selectedVariable];
        if (cmapSpacing) {
            drawTicksAndLabels(selectedVariable, reverse, minValue, maxValue, levels, ticks, decimals, units, bounded, cmapSpacing);
        }
    }, [cmapSpacing, selectedVariable]);
};

/**
 * Synchronize comparison map views when lockState is enabled
 */
export const useMapSyncEffect = (lockState, griddedMapRefs) => {
    useEffect(() => {
        let isSyncing = false;
        const currentRefs = griddedMapRefs.current;

        const handleMoveEnd = (index) => {
            return () => {
                if (isSyncing) return;
                isSyncing = true;

                const center = currentRefs[index].getCenter();
                const zoom = currentRefs[index].getZoom();

                currentRefs.forEach((map, i) => {
                    if (i !== index && map) {
                        map.jumpTo({ center, zoom });
                    }
                });

                isSyncing = false;
            };
        };

        const listeners = [];

        if (lockState) {
            currentRefs.forEach((map, index) => {
                if (map) {
                    const listener = handleMoveEnd(index);
                    map.on('moveend', listener);
                    listeners[index] = listener;
                }
            });
        }

        return () => {
            currentRefs.forEach((map, index) => {
                if (map && listeners[index]) {
                    map.off('moveend', listeners[index]);
                }
            });
        };
    }, [lockState, griddedMapRefs]);
};

/**
 * Add data layers when model/variable/timestep/init changes
 */
export const useAddDataEffect = (
    compareBool,
    griddedMapRefs,
    gridModels,
    selectedInit,
    selectedModel,
    selectedVariable,
    fhour,
    singleMapRef,
    popupRef,
    sliderPresLev,
    windState,
    initModel,
    mapInitialized,
    borderState,
    setModelFrameStatus    
) => {
    useEffect(() => {
        if (!(selectedModel && selectedInit && selectedVariable && mapInitialized)) return;
        $("#timeslider").slider("value", parseInt(fhour, 10));

        /*const callOnManager = (map, model) => {
            if (!map || !map._weatherManager) return;
            map._weatherManager.setActiveFillStatusCallback(
              setModelFrameStatus
            );
 
            map._weatherManager.setActive({
                init: selectedInit,
                model,
                variable: selectedVariable,
                fhour,
                presLev: sliderPresLev,
                windEnabled: windState,
                borderState,
                popupRef,
            });
        };*/
        const callOnManager = (map, model, panelKey) => {
          if (!map || !map._weatherManager) return;

          map._weatherManager.setActiveFillStatusCallback((frameStatus) => {
            setModelFrameStatus((prev) => ({
              ...prev,
              [panelKey]: frameStatus,
            }));
          });

          map._weatherManager.setActive({
            init: selectedInit,
            model,
            variable: selectedVariable,
            fhour,
            presLev: sliderPresLev,
            windEnabled: windState,
            borderState,
            popupRef,
          });
        };

        if (compareBool) {
            griddedMapRefs.current.forEach((map, idx) => {
                callOnManager(map, gridModels[idx], idx);
                console.log("GRID PANEL MODEL LOAD", {
                  panel: idx,
                  model: gridModels[idx],
                });
            });
        } else {
            callOnManager(singleMapRef.current, selectedModel, "single");
        }
    }, [compareBool, selectedModel, selectedVariable, mapInitialized, gridModels, fhour, sliderPresLev, windState, selectedInit, borderState, setModelFrameStatus]);
};

/**
 * Initialize maps when switching between single & comparison modes
 */
export const useMapInitializeEffect = (compareBool, singleMapRef, griddedMapRefs, popupRef, setMapInitialized, countyState) => {
    useEffect(() => {
        const initializeMap = async () => {
            setMapInitialized(false);
            if (compareBool) {
                await removeSingleMap(singleMapRef);
                await griddedMapsInitialize(['map-0', 'map-1', 'map-2', 'map-3'], griddedMapRefs, popupRef, countyState);
            } else {
                await removeGriddedMaps(griddedMapRefs);
                await singleMapInitialize('map', singleMapRef, popupRef, countyState);
            }
            setMapInitialized(true);
        };
        initializeMap();
    }, [compareBool]);
};

/**
 *  - Initializes sliders
 *  - Loads initialization options
 *  - Loads colormap & variable level configs
 */
export const useHomeEffect = (
    sliderLev,
    setSliderLev,
    sliderPresLev,
    setSliderPresLev,
    fhour,
    setFhour,
    setInitializationOptions,
    setSelectedInit,
    prevSelectedInitRef,
    setVariableLevels,
    setColormapColors
) => {
    useEffect(() => {
        windSlider(sliderLev, setSliderLev, sliderPresLev, setSliderPresLev);
        slider(fhour, setFhour);
        initializations(setInitializationOptions, setSelectedInit, prevSelectedInitRef);
        generateTimeZoneOptions();

        const fetchColormap = async () => {
            try {
                const levelsResponse = await fetch('/variable_levels.json');
                const levelsData = await levelsResponse.json();
                setVariableLevels(levelsData);

                const colormapResponse = await fetch('/colormaps.json');
                const colormapData = await colormapResponse.json();
                setColormapColors(colormapData);
            } catch (error) {
                console.error("Failed to load colormap or levels data", error);
            }
        };
        fetchColormap();
    }, []);
};

/**
 * Toggle county layer visibility on all maps
 */
export const useCountyEffect = (countyState, singleMapRef, griddedMapRefs) => {
    useEffect(() => {
        applyToAllMaps(singleMapRef, griddedMapRefs, (map) => {
            if (!countyState) {
                map.getStyle().layers.forEach(layer => {
                    if (layer.id === 'counties-layer') {
                        map.removeLayer(layer.id);
                        map.removeSource(layer.source);
                    }
                });
            } else {
                if (!map.getSource('us-counties')) {
                    map.addSource('us-counties', {
                        type: 'vector',
                        tiles: [
                            `${window.location.origin}/geojsons/counties/{z}/{x}/{y}.pbf`
                        ],
                        minzoom: 2,
                        maxzoom: 5
                    });
                }

                map.addLayer({
                    'id': 'counties-layer',
                    'type': 'line',
                    'source': 'us-counties',
                    'source-layer': 'counties',
                    'paint': {
                        'line-color': 'black',
                        'line-width': 0.10
                    }
                }, 'labels-layer');
            }
        });
    }, [countyState]);
};

/**
 * Toggle contour line (fill-outline) visibility on all maps
 */
export const useBorderEffect = (borderState, singleMapRef, griddedMapRefs) => {
    useEffect(() => {
        const outlineColor = borderState ? 'black' : 'transparent';
        applyToAllMaps(singleMapRef, griddedMapRefs, (map) => {
            map.getStyle().layers.forEach(layer => {
                if (layer.type === 'fill') {
                    map.setPaintProperty(layer.id, 'fill-outline-color', outlineColor);
                }
            });
        });
    }, [borderState]);
};

function hurricaneColor(knots) {
  const k = Number(knots);
  if (!Number.isFinite(k)) return '#9e9e9e';
  if (k < 34) return '#9e9e9e';
  if (k < 64) return '#3b82f6';
  if (k < 83) return '#eab308';
  if (k < 96) return '#f97316';
  if (k < 113) return '#ef4444';
  return '#7e22ce';
}

function parseDeckLatLon(value) {
  if (!value) return NaN;
  const s = String(value).trim();
  if (s.length < 2) return NaN;

  const hemi = s.slice(-1).toUpperCase();
  const mag = parseFloat(s.slice(0, -1)) / 10;
  if (!Number.isFinite(mag)) return NaN;

  return hemi === 'S' || hemi === 'W' ? -mag : mag;
}

function formatDeckValidTime(cycle, tau) {
  const s = String(cycle).trim();
  const h = parseInt(String(tau).trim(), 10);

  if (s.length !== 10 || !Number.isFinite(h)) return s;

  const year = parseInt(s.slice(0, 4), 10);
  const mon = parseInt(s.slice(4, 6), 10) - 1;
  const day = parseInt(s.slice(6, 8), 10);
  const hour = parseInt(s.slice(8, 10), 10);

  const d = new Date(Date.UTC(year, mon, day, hour));
  d.setUTCHours(d.getUTCHours() + h);

  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd} ${hh}Z`;
}

function parseHurricaneDeck(text) {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  return lines
    .map((line) => {
      const parts = line.split(',').map((p) => p.trim());
      const basin = parts[0] || '';
      const stormNum = parts[1] || '';
      const cycleRaw = parts[2] || '';
      const model = parts[4] || '';
      const tau = parts[5] || '';
      const latRaw = parts[6] || '';
      const lonRaw = parts[7] || '';
      const wind = parseFloat(parts[8]);
      const pressure = parseFloat(parts[9]);
      const stormType = parts[10] || '';
      const year = cycleRaw.slice(0, 4);  // "2026"
      const stormKey = `${year}_${basin}${stormNum}`; // "2026_WP04"
      const lat = parseDeckLatLon(latRaw);
      const lon = parseDeckLatLon(lonRaw);
      
      const windCode = parts[11] || '';
      const quadrantCode = parts[12] || '';

      let windRadii34 = null;

      if (windCode === '34' && quadrantCode === 'NEQ') {
        const NE = Number(parts[13]);
        const SE = Number(parts[14]);
        const SW = Number(parts[15]);
        const NW = Number(parts[16]);

        if ([NE, SE, SW, NW].some((v) => v > 0)) {
          windRadii34 = { NE, SE, SW, NW };
        }
      }

      return {
        basin,
        stormNum,
        stormId: `${basin}${stormNum}`,   // WP04
        stormKey: `${cycleRaw.slice(0, 4)}_${basin}${stormNum}`, // ✅ 2026_WP04
        cycleRaw,
        cycle: cycleRaw.slice(0, 8),
        model,                            // NAUR
        tau,
        lat,
        lon,
        latRaw,                           // 162N
        lonRaw,                           // 1448E
        intensity: wind,                  // 85
        pressure,                         // 940
        stormType,
        windRadii34,
        time: formatDeckValidTime(cycleRaw, tau),
      };
    })
    .filter((row) => Number.isFinite(row.lat) && Number.isFinite(row.lon));
}

function unwrapTrackPoints(points) {
  if (!points || points.length === 0) return points;

  const out = [{ ...points[0] }];

  for (let i = 1; i < points.length; i++) {
    const prev = out[i - 1];
    const curr = { ...points[i] };

    let lon = curr.lon;
    let diff = lon - prev.lon;

    if (diff > 180) {
      lon -= 360;
    } else if (diff < -180) {
      lon += 360;
    }

    curr.lon = lon;
    out.push(curr);
  }

  return out;
}

const EARTH_RADIUS_KM = 6371.0088;
const NM_TO_KM = 1.852;

function destinationPoint(lat, lon, bearingDeg, distanceNm) {
  const distanceKm = distanceNm * NM_TO_KM;
  const angularDistance = distanceKm / EARTH_RADIUS_KM;

  const bearing = bearingDeg * Math.PI / 180;
  const lat1 = lat * Math.PI / 180;
  const lon1 = lon * Math.PI / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
    Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing)
  );

  const lon2 = lon1 + Math.atan2(
    Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
    Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
  );

  return [
    lon2 * 180 / Math.PI,
    lat2 * 180 / Math.PI
  ];
}

function radiusForBearing(bearing, radii) {
  if (bearing >= 0 && bearing < 90) return radii.NE;
  if (bearing >= 90 && bearing < 180) return radii.SE;
  if (bearing >= 180 && bearing < 270) return radii.SW;
  return radii.NW;
}

function makeWindRadii34Polygon(lat, lon, radii) {
  if (!radii) return null;

  const ne = Number(radii.NE || 0);
  const se = Number(radii.SE || 0);
  const sw = Number(radii.SW || 0);
  const nw = Number(radii.NW || 0);

  if (Math.max(ne, se, sw, nw) <= 0) return null;

  const cleanRadii = { NE: ne, SE: se, SW: sw, NW: nw };
  const coords = [];

  // 0 = north, 90 = east, 180 = south, 270 = west
  for (let bearing = 0; bearing <= 360; bearing += 5) {
    const b = bearing === 360 ? 0 : bearing;
    const radiusNm = radiusForBearing(b, cleanRadii);

    if (radiusNm > 0) {
      coords.push(destinationPoint(lat, lon, b, radiusNm));
    } else {
      coords.push([lon, lat]);
    }
  }

  coords.push(coords[0]);

  return {
    type: 'Polygon',
    coordinates: [coords],
  };
}

function hurricaneGeoJSON(rows) {
  const groups = {};

  rows.forEach((r) => {
    const key = `${r.stormId}_${r.model}_${r.cycle}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  });

  Object.values(groups).forEach((pts) => {
    pts.sort((a, b) => a.tau - b.tau);
  });

  const features = [];

  Object.entries(groups).forEach(([key, pts]) => {
    const unwrappedPts = unwrapTrackPoints(pts);

    // line segments
    for (let i = 0; i < unwrappedPts.length - 1; i++) {
      const p1 = unwrappedPts[i];
      const p2 = unwrappedPts[i + 1];

      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [p1.lon, p1.lat],
            [p2.lon, p2.lat],
          ],
        },
        properties: {
          featureType: 'trackLine',
          group_key: key,
          storm_key: p2.stormKey,
          storm_name: p2.stormName,
          storm_type_name: p2.stormTypeName,
          storm_id: p2.stormId,
          basin: p2.basin,
          storm_num: p2.stormNum,
          model: p2.model,
          cycle: p2.cycle,
          tau: p2.tau,
          time: p2.time,
          intensity: p2.intensity,
          pressure: p2.pressure,
          storm_type: p2.stormType,
          color: hurricaneColor(p2.intensity),
        },
      });
    }

    // points
    unwrappedPts.forEach((p, idx) => {
      const raw = pts[idx];
      
      const windRadii34 = raw.windRadii34 || null;
     
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [p.lon, p.lat],
        },
        properties: {
          featureType: 'point',
          group_key: key,
          storm_key: raw.stormKey,
          storm_name: raw.stormName,
          storm_type_name: raw.stormTypeName,
          storm_id: raw.stormId,
          basin: raw.basin,
          storm_num: raw.stormNum,
          model: raw.model,
          cycle: raw.cycle,
          tau: raw.tau,
          label_text: raw.stormName
            ? `${raw.stormName} (${raw.stormId})`
            : raw.stormId,
          time: raw.time,
          intensity: raw.intensity,
          pressure: raw.pressure,
          storm_type: raw.stormType,
          lat: raw.lat,
          lon: raw.lon,
          lat_raw: raw.latRaw,
          lon_raw: raw.lonRaw,
          wind34_ne: windRadii34?.NE ?? 0,
          wind34_se: windRadii34?.SE ?? 0,
          wind34_sw: windRadii34?.SW ?? 0,
          wind34_nw: windRadii34?.NW ?? 0,
          has_wind34:
            windRadii34 &&
            [windRadii34.NE, windRadii34.SE, windRadii34.SW, windRadii34.NW]
              .some(v => Number(v) > 0)
              ? 1
              : 0,
          color: hurricaneColor(raw.intensity),
          is_latest: idx === pts.length - 1 ? 1 : 0,
        },
      });
      
      const radiiPolygon = makeWindRadii34Polygon(raw.lat, p.lon, windRadii34);

      if (radiiPolygon) {
        features.push({
          type: 'Feature',
          geometry: radiiPolygon,
          properties: {
            featureType: 'windRadii34',
            group_key: key,
            storm_key: raw.stormKey,
            storm_name: raw.stormName,
            storm_id: raw.stormId,
            basin: raw.basin,
            storm_num: raw.stormNum,
            model: raw.model,
            cycle: raw.cycle,
            tau: raw.tau,
            time: raw.time,
            intensity: raw.intensity,
            pressure: raw.pressure,
            wind34_ne: windRadii34.NE,
            wind34_se: windRadii34.SE,
            wind34_sw: windRadii34.SW,
            wind34_nw: windRadii34.NW,
          },
        });
      }
    });
  });

  return {
    type: 'FeatureCollection',
    features,
  };
}

async function fetchStormNames() {
  const namesUrl = `/geojsons/hurricane/index/storm_names.json`;

  const res = await fetch(namesUrl);

  if (!res.ok) {
    console.warn(`Could not load storm names: ${namesUrl}`, res.status);
    return {};
  }

  return await res.json();
}

async function fetchAllDeckRows(year, cycle, selectedDeckModel = '') {
  const listUrl = `/geojsons/hurricane/index/${year}/${cycle}.json?_=${Date.now()}`;

  const [listRes, stormNames] = await Promise.all([
    fetch(listUrl, { cache: "no-store" }),
    fetchStormNames()
  ]);

  if (!listRes.ok) {
    throw new Error(`Could not load hurricane index ${year}/${cycle}: ${listRes.status}`);
  }

  let fileUrls = await listRes.json();
  console.log("INDEX FILES BEFORE MODEL FILTER:", {
    year,
    cycle,
    selectedDeckModel,
    count: fileUrls.length,
    fileUrls,
  });

  if (selectedDeckModel) {
    fileUrls = fileUrls.filter((url) =>
      url.includes(`.${selectedDeckModel}.`)
    );
  }

  console.log("INDEX FILES AFTER MODEL FILTER:", {
    year,
    cycle,
    selectedDeckModel,
    count: fileUrls.length,
    fileUrls,
  });

  if (!Array.isArray(fileUrls) || !fileUrls.length) return [];

  if (selectedDeckModel) {
    fileUrls = fileUrls.filter((url) =>
      url.includes(`.${selectedDeckModel}.`)
    );
  }
  
  console.log("INDEX FILES AFTER MODEL FILTER:", {
    year,
    cycle,
    selectedDeckModel,
    count: fileUrls.length,
    fileUrls,
  });

  if (!fileUrls.length) return [];
  
  const results = await Promise.all(
    fileUrls.map(async (url) => {
      const bustUrl = `${url}${url.includes("?") ? "&" : "?"}_=${Date.now()}`;
      const res = await fetch(bustUrl, { cache: "no-store" });

      if (!res.ok) {
        console.warn('Skipping deck file:', url, res.status);
        return [];
      }

      const text = await res.text();
      const rows = parseHurricaneDeck(text);

      return rows.map((row) => {
        const stormMeta = stormNames[row.stormKey] || {};

        return {
          ...row,
          stormName: stormMeta.name || 'Unknown',
          stormTypeName: stormMeta.type || 'N/A',
        };
      });
    })
  );

  return results.flat();
}

function fitMapToGeoJSON(map, geojson) {
  const coords = geojson.features.flatMap((f) => {
    if (f.geometry.type === 'Point') return [f.geometry.coordinates];
    if (f.geometry.type === 'LineString') return f.geometry.coordinates;
    return [];
  });

  if (!coords.length) return;

  let minLon = coords[0][0];
  let minLat = coords[0][1];
  let maxLon = coords[0][0];
  let maxLat = coords[0][1];

  coords.forEach(([lon, lat]) => {
    minLon = Math.min(minLon, lon);
    minLat = Math.min(minLat, lat);
    maxLon = Math.max(maxLon, lon);
    maxLat = Math.max(maxLat, lat);
  });

  map.fitBounds(
    [
      [minLon, minLat],
      [maxLon, maxLat],
    ],
    {
      padding: 40,
      duration: 1000,
    }
  );
}

function formatDecimalLatLon(lat, lon) {
  const latNum = Number(lat);
  const lonNum = Number(lon);

  const latTxt = `${Math.abs(latNum).toFixed(1)}°${latNum >= 0 ? 'N' : 'S'}`;
  const lonTxt = `${Math.abs(lonNum).toFixed(1)}°${lonNum >= 0 ? 'E' : 'W'}`;

  return `${latTxt}, ${lonTxt}`;
}

function bindHurricanePopups(map) {
  if (map.__hurricanePopupBound) return;
  map.__hurricanePopupBound = true;

  map.on('click', 'hurricane-points', (e) => {
    const feature = e.features && e.features[0];
    if (!feature) return;

    // remove existing popup
    if (map.__hurricanePopup) {
      map.__hurricanePopup.remove();
      map.__hurricanePopup = null;
    }    

    const p = feature.properties || {};
    const coords = feature.geometry.coordinates.slice();

    const html = `
      <div style="font-size:13px; line-height:1.5; min-width:180px;">
        <div style="font-weight:700; margin-bottom:6px;">Storm Track Point</div>
        <div style="font-weight:700; margin-bottom:6px; color:${p.color}">
          <b>Name - ID:</b> ${p.storm_name || 'Unknown Storm'} - ${p.storm_id || ''} 
        </div>
        <div><b>Type:</b> ${p.storm_type_name || 'N/A'}</div>
        <div><b>Model:</b> ${p.model || ''}</div>
        <div><b>Fcst Hour:</b> ${p.tau || ''}</div>
        <div><b>Lat/Lon:</b> ${formatDecimalLatLon(p.lat, p.lon)}</div>
        <div><b>Intensity:</b> ${p.intensity || ''} kt</div>
        <div><b>Pressure:</b> ${p.pressure || ''} mb</div>
     </div>
    `;
    
    map.__hurricanePopup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: true,
      offset: 12,
    })
      .setLngLat(coords)
      .setHTML(html)
      .addTo(map);
  });

  map.on('mouseenter', 'hurricane-points', () => {
    map.getCanvas().style.cursor = 'pointer';
  });

  map.on('mouseleave', 'hurricane-points', () => {
    map.getCanvas().style.cursor = '';
  });
}

// To determine the selected model 
function mapModel(selectedModel) {
  if (!selectedModel) {
    console.warn('⚠️ selectedModel is empty or undefined');
    return '';
  }

  const map = {
    au_ifs: 'EAUR',
    au_gfs: 'NAUR',
    pw_ifs: 'EPNG',
    pw_gfs: 'NPNG',
    gc_ifs: 'EGRP',
    gc_gfs: 'NGRP',
    fcnv2_ifs: 'EFOR',
    fcnv2_gfs: 'NFOR',
    gfs_gfs: 'GFS',
    ifs_ifs: 'IFS',
    aifs_aifs: 'AIFS',
  };

  const result = map[selectedModel];

  if (!result) {
    console.warn('⚠️ No deck model mapping for:', selectedModel);
  } else {
    console.log('✅ Model mapping:', selectedModel, '→', result);
  }

  return result || '';
}

function removeHurricanePopup(map) {
  if (map && map.__hurricanePopup) {
    map.__hurricanePopup.remove();
    map.__hurricanePopup = null;
  }
}

function selectedInitToTimestamp(selectedInit) {
  const s = String(selectedInit || "").trim();

  if (!/^\d{8}_\d{2}$/.test(s)) return NaN;

  const year = Number(s.slice(0, 4));
  const month = Number(s.slice(4, 6)) - 1;
  const day = Number(s.slice(6, 8));
  const hour = Number(s.slice(9, 11));

  return Date.UTC(year, month, day, hour);
}

function hurricaneRowValidTimestamp(row) {
  const cycle = String(row?.cycleRaw || "").trim();
  const tau = Number.parseInt(String(row?.tau || "").trim(), 10);

  if (!/^\d{10}$/.test(cycle) || !Number.isFinite(tau)) {
    return NaN;
  }

  const year = Number(cycle.slice(0, 4));
  const month = Number(cycle.slice(4, 6)) - 1;
  const day = Number(cycle.slice(6, 8));
  const hour = Number(cycle.slice(8, 10));

  return Date.UTC(year, month, day, hour) + tau * 60 * 60 * 1000;
}

export const useHurricaneEffect = (
  hurricaneState,
  hurricaneYear,
  hurricaneCycle,
  selectedModel,
  hurricaneTrackMode,
  setHurricaneModelAvailable,
  hurricaneRadii34State,
  setHasWindRadii34,
  singleMapRef,
  griddedMapRefs,
  mapInitialized,
  compareBool,
  gridModels,
  setSelectedModelStorms,
  setAllModelStorms,
  selectedStormIds,
  setSelectedStormIds,
  hurricaneRows,
  setHurricaneRows,
  selectedInit,
  fhour,
  selectedVariable,
  modelFrameStatus,
  setHurricaneState,
  setHurricaneNoModelData
) => {
  useEffect(() => {
    const currentGridMaps = griddedMapRefs.current;
    const currentSingleMap = singleMapRef.current;
    console.log("HURRICANE EFFECT FIRED:", {
      hurricaneState,
      hurricaneYear,
      hurricaneCycle,
      selectedModel,
      hurricaneTrackMode,
      mapInitialized,
    });
    //if (!mapInitialized) return;
    //if (!hurricaneYear || !hurricaneCycle) return;
    //let cancelled = false;
 
    if (!mapInitialized) return;

    if (!hurricaneYear || !hurricaneCycle) return;

    const initializationTimestamp = selectedInitToTimestamp(selectedInit);
    const forecastHour = Number.parseInt(String(fhour), 10);

    const modelValidTimestamp =
      Number.isFinite(initializationTimestamp) &&
      Number.isFinite(forecastHour)
        ? initializationTimestamp + forecastHour * 60 * 60 * 1000
        : NaN;

    const modelTimeIsForecast =
      Number.isFinite(modelValidTimestamp) &&
      Number.isFinite(initializationTimestamp) &&
      modelValidTimestamp > initializationTimestamp;

    /*console.log("HURRICANE TIME / MODEL DATA CHECK:", {
      selectedInit,
      selectedModel,
      selectedVariable,
      fhour,
      initializationTimestamp,
      modelValidTimestamp,
      modelTimeIsForecast,
      expectedModelFrameKey,
      modelFrameStatus,
      activeModelFrameLoaded,
    });    

    const expectedModelFrameKey =
        selectedInit &&
        selectedModel &&
        selectedVariable &&
        fhour
            ? `fill::${selectedInit}::${selectedModel}::${selectedVariable}::${fhour}`
            : null;

    const activeModelFrameLoaded =
        modelFrameStatus?.key === expectedModelFrameKey &&
        modelFrameStatus?.status === "loaded";
    */

    let cancelled = false;   
 
    const removeHurricane = (map) => {
      if (!map) return;

      removeHurricanePopup(map);

      try { if (map.getLayer('hurricane-lines')) map.removeLayer('hurricane-lines'); } catch {}
      try { if (map.getLayer('hurricane-points')) map.removeLayer('hurricane-points'); } catch {}
      try { if (map.getLayer('hurricane-t0-labels')) map.removeLayer('hurricane-t0-labels'); } catch {}
      try { if (map.getLayer('hurricane-wind-radii-34-outline')) map.removeLayer('hurricane-wind-radii-34-outline'); } catch {}
      try { if (map.getLayer('hurricane-wind-radii-34-fill')) map.removeLayer('hurricane-wind-radii-34-fill'); } catch {}
      try { if (map.getSource('hurricane-tracks')) map.removeSource('hurricane-tracks'); } catch {}
    };


    const resetHurricaneUI = (map, index = null) => {
      setSelectedStormIds([]);
      setSelectedModelStorms([]);
      setAllModelStorms([]);
      setHurricaneRows([]);

      if (compareBool && index !== null) {
        setHurricaneModelAvailable((prev) => {
          const updated = Array.isArray(prev)
            ? [...prev]
            : [true, true, true, true];

          updated[index] = false;
          return updated;
        });
      } else {
        setHurricaneModelAvailable(false);
      }

      removeHurricane(map);

      /*map.flyTo({
        center: compareBool ? [-95, 40] : [-90, 40],
        zoom: compareBool ? 2 : 3,
        duration: 1500,
      });*/
    };

    const addHurricane = async (map, index = null) => {
      if (!map) return;

      console.log("ADD HURRICANE CALLED:", {
        hurricaneYear,
        hurricaneCycle,
        selectedModel,
        mapLoaded: map.loaded(),
        styleLoaded: map.isStyleLoaded(),
      });
      
      const getActiveHurricaneMaps = (map) => {
        if (compareBool && griddedMapRefs?.current?.length) {
          return griddedMapRefs.current.filter(Boolean);
        }

        return map ? [map] : [];
      };

      /*const zoomToStormBounds = (map, geojson, compareBool = false) => {
        if (!map || !geojson?.features?.length) return;

        const coords = [];

        geojson.features.forEach((feature) => {
          const geometry = feature.geometry;
          if (!geometry) return;

          if (geometry.type === "LineString") {
            coords.push(...geometry.coordinates);
          }

          if (geometry.type === "MultiLineString") {
            geometry.coordinates.forEach((line) => coords.push(...line));
          }
        });

        if (!coords.length) return;

        const lons = coords.map((c) => c[0]);
        const lats = coords.map((c) => c[1]);

        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const centerLat = (minLat + maxLat) / 2;

        const hasAtlantic = lons.some((lon) => lon >= -100 && lon <= 20);
        const hasPacific = lons.some((lon) => lon < -100 || lon > 100);

        let centerLon = -90;

        if (hasAtlantic && !hasPacific) {
          centerLon = -50;
        } else if (hasPacific && !hasAtlantic) {
          centerLon = compareBool ? 180 : -170;
        } else {
          centerLon = -140;
        }

        map.flyTo({
          center: [centerLon, centerLat ?? 20],
          zoom: compareBool ? 1 : 2,
          duration: 1500,
        });
      };*/
 
      /*const resetHurricaneView = () => {
        if (singleMapRef?.current) {
          singleMapRef.current.flyTo({
            center: [-90, 40],
            zoom: 3,
            duration: 1500,
          });
        }

        if (griddedMapRefs?.current) {
          griddedMapRefs.current.forEach((map) => {
            if (map) {
              map.flyTo({
                center: [-95, 40],
                zoom: 2,
                duration: 1500,
              });
            }
          });
        }
      };*/

      const doAdd = async () => {
        try {
          if (!map || map._removed) return;

          if (!map.getStyle()) {
            console.warn("No map style available yet, skipping hurricane add");
            return;
          }

          /*removeHurricane(map);

          // Hurricane tracks should not display when the model valid time
          // is later than the selected model initialization time.
          if (modelTimeIsForecast) {
            console.log("Hiding hurricane track for forecast model time:", {
              selectedInit,
              fhour,
            });

            return;
          }*/

          removeHurricane(map);

          const modelForThisMap = compareBool
            ? gridModels[index]
            : selectedModel;

          const panelKey = compareBool ? index : "single";

          const expectedModelFrameKey =
            selectedInit &&
            modelForThisMap &&
            selectedVariable &&
            fhour
              ? `fill::${selectedInit}::${modelForThisMap}::${selectedVariable}::${fhour}`
              : null;

          const frameStatus = modelFrameStatus?.[panelKey];

          const activeModelFrameLoaded =
            frameStatus?.key === expectedModelFrameKey &&
            frameStatus?.status === "loaded";
        
          const activeModelFrameMissing =
            frameStatus?.key === expectedModelFrameKey &&
            frameStatus?.status === "error";   

          console.log("HURRICANE TIME / MODEL DATA CHECK:", {
            index,
            panelKey,
            selectedInit,
            modelForThisMap,
            selectedVariable,
            fhour,
            initializationTimestamp,
            modelValidTimestamp,
            modelTimeIsForecast,
            expectedModelFrameKey,
            frameStatus,
            activeModelFrameLoaded,
          });

          // The hurricane overlay belongs to the model data displayed
          // on this specific map. Keep it hidden until this exact frame
          // has successfully loaded.
          /*if (!activeModelFrameLoaded) {
            console.log("Hiding hurricane track: model frame is not loaded", {
              index,
              panelKey,
              expectedModelFrameKey,
              frameStatus,
            });

            return;
          }*/

          if (activeModelFrameMissing) {
            console.log("No model data available for storm tracks", {
              index,
              panelKey,
              expectedModelFrameKey,
              frameStatus,
            });

            removeHurricane(map);

            // Only change the sidebar checkbox in single-map mode.
            // Comparison panels share one sidebar, so a missing frame in one
            // panel must not disable storm tracks for all four panels.
            if (!compareBool) {
              setHurricaneNoModelData(true);
            }

            return;
          }

          if (!activeModelFrameLoaded) {
            // Frame is still loading. Hide stale tracks, but do NOT
            // change the user's Storm Track(s) selection.
            removeHurricane(map);
            return;
          } 
         
          if (!compareBool) {
            setHurricaneNoModelData(false);
          }

          //removeHurricane(map);

          //const modelForThisMap = compareBool
          //  ? gridModels[index]
          //  : selectedModel;

          const selectedDeckModel = mapModel(modelForThisMap);

          const hurricaneUnsupportedModels = ["gfs_gfs", "ifs_ifs", "aifs_aifs"];

          if (hurricaneUnsupportedModels.includes(modelForThisMap)) {
            console.log("Skipping hurricane tracks for unsupported model:", modelForThisMap);

            if (compareBool) {
              setHurricaneModelAvailable((prev) => {
                const updated = Array.isArray(prev) ? [...prev] : [true, true, true, true];
                updated[index] = false;
                return updated;
              });
            } else {
              setHurricaneModelAvailable(false);
            }

            removeHurricane(map);
            return;
          }
          
          console.log("HURRICANE MAP MODEL:", {
            index,
            compareBool,
            modelForThisMap,
            selectedDeckModel,
          });

          const rows = await fetchAllDeckRows(hurricaneYear, hurricaneCycle, hurricaneTrackMode === 'all' ? '' : selectedDeckModel);
          
          if (cancelled) return;          
          console.log("HURRICANE ROWS LOADED:", {
            count: rows.length,
            firstRow: rows[0],
            hurricaneYear,
            hurricaneCycle,
            selectedModel: modelForThisMap,
            selectedDeckModel,
            hurricaneTrackMode,
          });
          
          setHurricaneRows(rows);

          const modelHasTracks = rows.length > 0;

          if (compareBool) {
            setHurricaneModelAvailable((prev) => {
              const updated = Array.isArray(prev) ? [...prev] : [true, true, true, true];
              updated[index] = modelHasTracks;
              return updated;
            });
          } else {
            setHurricaneModelAvailable(modelHasTracks);
          }

          if (!rows.length) {
            console.warn('No hurricane rows parsed for:', {
              hurricaneYear,
              hurricaneCycle,
              selectedModel,
              selectedDeckModel,
              hurricaneTrackMode,
            });

            resetHurricaneUI(map, index);
            return;
          }        

          console.log('Hurricane track mode:', hurricaneTrackMode);
          console.log('Selected UI model:', selectedModel);
          console.log('Selected deck model:', selectedDeckModel);

          const normalizeStormId = (value) =>
            String(value ?? "")
              .trim()
              .toUpperCase();

          const getRowStormId = (row) => {
            if (row.storm_id) return normalizeStormId(row.storm_id);
            if (row.stormId) return normalizeStormId(row.stormId);

            if (row.basin && row.stormNum) {
              return normalizeStormId(
                `${String(row.basin).trim()}${String(row.stormNum).trim().padStart(2, "0")}`
              );
            }

            return "";
          };
         
          // Build from ALL rows
          //const fullGeojson = hurricaneGeoJSON(rows);
       
          // Only include hurricane positions valid at or before
          // the currently displayed model valid time.
          const visibleRows = rows.filter((row) => {
            const rowValidTimestamp = hurricaneRowValidTimestamp(row);

            return (
              Number.isFinite(rowValidTimestamp) &&
              Number.isFinite(modelValidTimestamp) &&
              rowValidTimestamp <= modelValidTimestamp
            );
          });

          console.log("HURRICANE TIME FILTER:", {
            selectedInit,
            fhour,
            totalRows: rows.length,
            visibleRows: visibleRows.length,
          });

          // Build the displayed track only from rows valid at this model time.
          const fullGeojson = hurricaneGeoJSON(visibleRows);   

          const trackFeatureCount = fullGeojson.features.filter(
            (f) =>
              f.geometry?.type === "LineString" ||
              f.properties?.featureType === "trackLine"
          ).length;

          /*if (hurricaneTrackMode === "selected" && trackFeatureCount === 0) {
            console.warn("No storm tracks available; resetting view", {
              rows: rows.length,
              trackFeatureCount,
              selectedModel,
              selectedDeckModel,
              compareBool,
              index,
            });

            resetHurricaneUI(map, index);
            return;
          }*/

          if (hurricaneTrackMode === "selected" && visibleRows.length === 0) {
            console.warn("No hurricane positions available at this displayed time", {
              rows: rows.length,
              visibleRows: visibleRows.length,
              selectedModel: modelForThisMap,
              selectedDeckModel,
              compareBool,
              index,
            });

            removeHurricane(map);
            return;
          }

          // Build checkbox list from ALL storms
          const stormMap = new Map();

          fullGeojson.features.forEach((feature) => {
            const stormId = feature.properties?.storm_id;
            const stormName = feature.properties?.storm_name || stormId;

            if (!stormId) return;

            stormMap.set(String(stormId), {
              storm_id: String(stormId),
              storm_name: stormName,
            });
          });

          const storms = Array.from(stormMap.values());

          if (hurricaneTrackMode === "all") {
            setAllModelStorms(storms);
          } else {
            setSelectedModelStorms(storms);
          }

          setSelectedStormIds((prev = []) => {
            if (prev.length > 0) return prev;
            return storms.map((storm) => String(storm.storm_id));
          });
 
          const geojson = fullGeojson;
          
          console.log(
            'T0 point labels:',
            geojson.features
              .filter(
                f =>
                  f.geometry?.type === 'Point' &&
                  String(f.properties?.tau) === '000'
              )
              .map(f => ({
                tau: f.properties?.tau,
                label: f.properties?.label_text,
                storm: f.properties?.storm_id
              }))
          );
  
          console.log(
            'Sample labeled feature:',
            geojson.features.find(
            f => f.geometry?.type === 'Point'
            )?.properties
          );
        
          console.log("selectedDeckModel:", selectedDeckModel);
          console.log(
            "row models:",
            [...new Set(rows.map((r) => r.model || r.model_id || r.deck_model))]
          );
          console.log("sample hurricane row:", rows[0]);
 
          const labelFeaturesByStorm = new Map();

          geojson.features
            .filter(
              (feature) =>
                feature.geometry?.type === "Point" &&
                String(feature.properties?.tau).padStart(3, '0') === "000"
            )
            .forEach((feature) => {
              const stormId = String(feature.properties?.storm_id || "");

              if (!stormId) return;

              // Keep one forecast-hour-000 label per storm
              if (!labelFeaturesByStorm.has(stormId)) {
                labelFeaturesByStorm.set(stormId, feature);
              }
            });
          const t0LabelFeatures = Array.from(labelFeaturesByStorm.values());

          const t0LabelGeoJSON = {
            type: "FeatureCollection",
            features: t0LabelFeatures,
          };       

          const hasWindRadii34 = geojson.features.some(
            f => f.properties?.featureType === 'windRadii34'
          );

          setHasWindRadii34(hasWindRadii34);

          console.log('Has 34 kt radii:', hasWindRadii34);

          console.log('Sample hurricane row:', rows[0]);

          console.log(
            'Rows with windRadii34:',
            rows.filter(r => r.windRadii34).length
          );

          console.log(
            'Rows with nonzero windRadii34:',
            rows.filter(r =>
              r.windRadii34 &&
              (
                Number(r.windRadii34.NE) > 0 ||
                Number(r.windRadii34.SE) > 0 ||
                Number(r.windRadii34.SW) > 0 ||
                Number(r.windRadii34.NW) > 0
              )
            ).length
          );

          console.log(
            'Wind radii polygon features:',
            geojson.features.filter(
              f => f.properties?.featureType === 'windRadii34'
            ).length
          );

          removeHurricanePopup(map);         

          if (map.getLayer('hurricane-wind-radii-34-fill')) map.removeLayer('hurricane-wind-radii-34-fill');
          if (map.getLayer('hurricane-wind-radii-34-outline')) map.removeLayer('hurricane-wind-radii-34-outline');
          if (map.getLayer('hurricane-lines')) map.removeLayer('hurricane-lines');
          if (map.getLayer('hurricane-points')) map.removeLayer('hurricane-points');
          if (map.getLayer('hurricane-t0-labels')) map.removeLayer('hurricane-t0-labels');
          if (map.getSource('hurricane-tracks')) map.removeSource('hurricane-tracks');

          map.addSource('hurricane-tracks', {
            type: 'geojson',
            data: geojson,
          });

          if (map.getSource("hurricane-t0-labels-source")) {
            map.getSource("hurricane-t0-labels-source").setData(t0LabelGeoJSON);
          } else {
            map.addSource("hurricane-t0-labels-source", {
              type: "geojson",
              data: t0LabelGeoJSON,
            });
          }   
 
          console.log(
            'Setting radii visibility:',
            hurricaneRadii34State ? 'visible' : 'none'
          );        

          // 34 kt wind radii fill
          map.addLayer({
            id: 'hurricane-wind-radii-34-fill',
            type: 'fill',
            source: 'hurricane-tracks',
            filter: ['==', ['get', 'featureType'], 'windRadii34'],
            layout: {
              visibility:
                hurricaneRadii34State && hasWindRadii34
                  ? 'visible'
                  : 'none',
            },
            paint: {
              'fill-color': '#ff66cc',
              'fill-opacity': 0.25,
            },
          });

          // 34 kt wind radii outline
          map.addLayer({
            id: 'hurricane-wind-radii-34-outline',
            type: 'line',
            source: 'hurricane-tracks',
            filter: ['==', ['get', 'featureType'], 'windRadii34'],
            layout: {
              visibility:
                hurricaneRadii34State && hasWindRadii34
                  ? 'visible'
                  : 'none',
            },
            paint: {
              'line-color': '#ff66cc',
              'line-width': 1,
              'line-opacity': 0.8,
            },
          });

          map.addLayer({
            id: 'hurricane-lines',
            type: 'line',
            source: 'hurricane-tracks',
            filter: ['==', ['geometry-type'], 'LineString'],
            paint: {
              'line-color': ['get', 'color'],
              'line-width': 4,
              'line-opacity': 0.95,
            },
          });

          map.addLayer({
            id: 'hurricane-points',
            type: 'circle',
            source: 'hurricane-tracks',
            filter: ['==', ['geometry-type'], 'Point'],
            paint: {
              'circle-radius': ['case', ['==', ['get', 'is_latest'], 1], 6, 4],
              'circle-color': ['get', 'color'],
              'circle-stroke-color': '#111',
              'circle-stroke-width': 1,
              'circle-opacity': 0.95,
            },
          });

          map.addLayer({
            id: 'hurricane-t0-labels',
            type: 'symbol',
            source: 'hurricane-t0-labels-source',
            layout: {
              'text-field': [
                'concat',
                ['get', 'storm_name'],
                ' (',
                ['get', 'storm_id'],
                ')'
              ],
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                2, 14,
                4, 16,
                6, 18,
                8, 20
              ],
              'text-anchor': 'bottom',
              'text-offset': [0, -1.2],
              'text-allow-overlap': false,
              'text-ignore-placement': false,
            },
            paint: {
              'text-color': '#ffff00',
              'text-halo-color': '#000000',
              'text-halo-width': 3,
              'text-halo-blur': 1,
            },
          });

          map.moveLayer('hurricane-lines');
          map.moveLayer('hurricane-points');
          map.moveLayer('hurricane-t0-labels');
 
          bindHurricanePopups(map);
          if (
            hurricaneState &&
            geojson.features.length > 0 &&
            rows.length > 0
          ) {
            //zoomToStormBounds(map, geojson, compareBool);
           } 
        } catch (err) {
          console.warn('Hurricane overlay failed:', err);

          const isMissingHurricaneIndex =
            String(err?.message || "").includes("Could not load hurricane index") ||
            String(err?.message || "").includes("404");

          if (isMissingHurricaneIndex) {
            resetHurricaneUI(map, index);
            return;
          }
  
          if (compareBool) {
            setHurricaneModelAvailable((prev) => {
              const updated = Array.isArray(prev) ? [...prev] : [true, true, true, true];
              updated[index] = false;
              return updated;
            });
          } else {
            setHurricaneModelAvailable(false);
          }
          removeHurricane(map); 
        }
      };

      const runAddWhenReady = async () => {
        if (cancelled) return;
        if (!map || map._removed) return;
        
        if (map.getStyle()) {
          await doAdd();
          return;
        }

        map.once("styledata", async () => {
          if (cancelled) return;
          if (!map || map._removed) return;
          await doAdd();
        });
      };
      await runAddWhenReady();
    };

    if (hurricaneState) {
      if (compareBool) {
        griddedMapRefs.current.forEach((map, index) => {
          addHurricane(map, index);
        });
      } else {
        addHurricane(singleMapRef.current);
      }
    } else {
      if (compareBool) {
        griddedMapRefs.current.forEach((map, index) => {
          removeHurricane(map);
        });
      } else {
        removeHurricane(singleMapRef.current);
      }
    }
    return () => {
      cancelled = true;

      if (compareBool) {
        currentGridMaps.forEach((map) => {
          removeHurricane(map);
        });
      } else {
        removeHurricane(currentSingleMap);
      }
    };
  }, [
    hurricaneState,
    hurricaneYear,
    hurricaneCycle,
    selectedModel,
    hurricaneTrackMode,
    setHurricaneModelAvailable,
    hurricaneRadii34State,
    setHasWindRadii34,
    mapInitialized,
    singleMapRef,
    griddedMapRefs,
    compareBool,
    gridModels,
    setSelectedModelStorms,
    setAllModelStorms,
    selectedInit, 
    fhour, 
    selectedVariable,
    modelFrameStatus,
    setHurricaneState,
    setHurricaneNoModelData
  ]);

  useEffect(() => {
    if (compareBool) return;
    if (!hurricaneState) return;
    if (!hurricaneRows?.length) return;

    const normalizeStormId = (value) =>
      String(value ?? "").trim().toUpperCase();

    const getRowStormId = (row) => {
      if (row.storm_id) return normalizeStormId(row.storm_id);
      if (row.stormId) return normalizeStormId(row.stormId);

      if (row.basin && row.stormNum) {
        return normalizeStormId(
          `${String(row.basin).trim()}${String(row.stormNum).trim().padStart(2, "0")}`
        );
      }  

      return "";
    };

    const safeSelectedStormIds =
      (selectedStormIds || []).map(normalizeStormId);

    const filteredRows =
      safeSelectedStormIds.length > 0
        ? hurricaneRows.filter((row) =>
            safeSelectedStormIds.includes(getRowStormId(row))
          )
        : [];

    const filteredGeojson = hurricaneGeoJSON(filteredRows);

    const updateMap = (map) => {
      if (!map?.getSource) return;
     
      const trackSource = map.getSource("hurricane-tracks");
      if (trackSource) {
        trackSource.setData(filteredGeojson);
      }
    
      const labelFeaturesByStorm = new Map();

      filteredGeojson.features
        .filter((feature) => feature.geometry?.type === "Point")
        .forEach((feature) => {
          const stormId = String(feature.properties?.storm_id || "");
          const tau = Number(feature.properties?.tau ?? 0);

          if (!stormId) return;

          const existing = labelFeaturesByStorm.get(stormId);
          const existingTau = Number(existing?.properties?.tau ?? -1);

          if (!existing || tau > existingTau) {
            labelFeaturesByStorm.set(stormId, feature);
          }
        });

      const filteredT0LabelGeoJSON = {
        type: "FeatureCollection",
        features: Array.from(labelFeaturesByStorm.values()),
      };

      const labelSource = map.getSource("hurricane-t0-labels-source");

      if (labelSource) {
        labelSource.setData(filteredT0LabelGeoJSON);
      }
    };

    updateMap(singleMapRef?.current);

    griddedMapRefs?.current?.forEach(updateMap);

    console.log("CHECKBOX UPDATE", {
      selectedStormIds: safeSelectedStormIds,
      rows: hurricaneRows.length,
      filteredRows: filteredRows.length,
    });

  }, [
    selectedStormIds,
    hurricaneRows,
    hurricaneState,
    compareBool
  ]);
};
